import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle, Send, Trash2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/hr/expense-claims/$claimId')({
  component: ClaimDetailPage,
})

function ClaimDetailPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <ClaimDetail />
    </ModuleGuard>
  )
}

type ExpenseItem = {
  id: number
  description: string
  amount: string | number
  category?: string | null
  date?: string | null
}

type ClaimData = {
  id: number
  employeeId: number
  title: string
  description?: string | null
  status: string
  currency?: string | null
  totalAmount?: string | null
  submittedAt?: string | null
  approvedAt?: string | null
  paidAt?: string | null
  rejectionReason?: string | null
  items?: ExpenseItem[]
  employee?: { firstName: string; lastName: string } | null
}

const rejectSchema = z.object({
  reason: z.string().min(1, 'Rejection reason is required'),
})

type RejectFormValues = z.infer<typeof rejectSchema>

const CATEGORY_COLORS: Record<string, string> = {
  travel: 'bg-blue-100 text-blue-800',
  meals: 'bg-orange-100 text-orange-800',
  accommodation: 'bg-purple-100 text-purple-800',
  office_supplies: 'bg-gray-100 text-gray-700',
  equipment: 'bg-yellow-100 text-yellow-800',
  training: 'bg-green-100 text-green-800',
  entertainment: 'bg-pink-100 text-pink-800',
  other: 'bg-gray-100 text-gray-600',
}

function ClaimDetail() {
  const params = Route.useParams()
  const claimId = Number(params.claimId)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: claimData, isLoading } = useQuery(
    orpc.hr.getExpenseClaim.queryOptions({ input: { id: claimId } }),
  )

  const claim = claimData as ClaimData | null | undefined

  const submitMutation = useMutation(
    orpc.hr.submitExpenseClaim.mutationOptions({
      onSuccess: () => {
        toast.success('Claim submitted for approval')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getExpenseClaim']] })
        queryClient.invalidateQueries({ queryKey: [['hr', 'listExpenseClaims']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const approveMutation = useMutation(
    orpc.hr.approveExpenseClaim.mutationOptions({
      onSuccess: () => {
        toast.success('Claim approved')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getExpenseClaim']] })
        queryClient.invalidateQueries({ queryKey: [['hr', 'listExpenseClaims']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const rejectMutation = useMutation(
    orpc.hr.rejectExpenseClaim.mutationOptions({
      onSuccess: () => {
        toast.success('Claim rejected')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getExpenseClaim']] })
        queryClient.invalidateQueries({ queryKey: [['hr', 'listExpenseClaims']] })
        setRejectDialogOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const markPaidMutation = useMutation(
    orpc.hr.markExpenseClaimPaid.mutationOptions({
      onSuccess: () => {
        toast.success('Claim marked as paid')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getExpenseClaim']] })
        queryClient.invalidateQueries({ queryKey: [['hr', 'listExpenseClaims']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteExpenseClaim.mutationOptions({
      onSuccess: () => {
        toast.success('Claim deleted')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listExpenseClaims']] })
        void navigate({ to: '/hr/expense-claims' })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const fmt = (val?: string | null | number) =>
    val != null
      ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })
      : '—'

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!claim) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-muted-foreground">Expense claim not found</p>
      </div>
    )
  }

  const empName = claim.employee
    ? `${claim.employee.firstName} ${claim.employee.lastName}`
    : `Employee #${claim.employeeId}`

  const items = (claim.items ?? []) as ExpenseItem[]

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/hr/expense-claims">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Claims
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{claim.title}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-muted-foreground">{empName}</span>
            <StatusBadge status={claim.status} />
            <Badge variant="outline">{claim.currency ?? 'USD'}</Badge>
          </div>
          {claim.description && (
            <p className="text-sm text-muted-foreground mt-2">{claim.description}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Total Amount</p>
          <p className="text-2xl font-bold">{fmt(claim.totalAmount)}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        {claim.status === 'draft' && (
          <>
            <Button
              onClick={() => submitMutation.mutate({ id: claimId })}
              disabled={submitMutation.isPending}
            >
              <Send className="h-4 w-4 mr-2" />
              {submitMutation.isPending ? 'Submitting...' : 'Submit'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </>
        )}
        {claim.status === 'submitted' && (
          <>
            <Button
              onClick={() => approveMutation.mutate({ id: claimId })}
              disabled={approveMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {approveMutation.isPending ? 'Approving...' : 'Approve'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setRejectDialogOpen(true)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
          </>
        )}
        {claim.status === 'approved' && (
          <Button
            onClick={() => markPaidMutation.mutate({ id: claimId })}
            disabled={markPaidMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {markPaidMutation.isPending ? 'Marking...' : 'Mark Paid'}
          </Button>
        )}
      </div>

      {/* Rejection reason */}
      {claim.status === 'rejected' && claim.rejectionReason && (
        <Card className="mb-6 border-red-200">
          <CardHeader>
            <CardTitle className="text-sm text-red-700">Rejection Reason</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{claim.rejectionReason}</p>
          </CardContent>
        </Card>
      )}

      {/* Expense Items */}
      <Card>
        <CardHeader>
          <CardTitle>Expense Items</CardTitle>
          <CardDescription>{items.length} item{items.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No items</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    <TableCell>
                      {item.category ? (
                        <Badge
                          variant="outline"
                          className={`${CATEGORY_COLORS[item.category] ?? 'bg-gray-100 text-gray-600'} capitalize`}
                        >
                          {item.category.replace('_', ' ')}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {item.date ? new Date(item.date).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium">{fmt(item.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={3} className="font-semibold text-right">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-bold">{fmt(claim.totalAmount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <RejectDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        isPending={rejectMutation.isPending}
        onSubmit={(reason) => rejectMutation.mutate({ id: claimId, reason })}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Claim</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense claim? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate({ id: claimId })}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface RejectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isPending: boolean
  onSubmit: (reason: string) => void
}

function RejectDialog({ open, onOpenChange, isPending, onSubmit }: RejectDialogProps) {
  const form = useForm<RejectFormValues>({
    resolver: zodResolver(rejectSchema),
    defaultValues: { reason: '' },
  })

  const handleSubmit = (data: RejectFormValues) => {
    onSubmit(data.reason)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Reject Claim</DialogTitle>
          <DialogDescription>Provide a reason for rejecting this expense claim</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason *</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Reason for rejection..." rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={isPending}>
                {isPending ? 'Rejecting...' : 'Reject Claim'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
